import { Injectable, effect } from '@angular/core'
import {
  connect,
  disconnect,
  getAccount,
  watchAccount,
  watchChainId,
  signMessage,
  getWalletClient,
  signTypedData
} from '@wagmi/core'
import { hexToSignature, type Hex, type WalletClient } from 'viem'
import { ENABLE_TRADING_SIGNATURE } from '../common/constants/signature.constants'
import { ToastService } from './toast.service'
import { AppKitService } from './app-kit.service'

@Injectable({ providedIn: 'root' })
export class WalletService {

  constructor(
    private toast: ToastService,
    private appKitService: AppKitService
  ) {
    const that = this;

    // Watch for network changes using AppKit signals
    effect(() => {
      const state = this.appKitService.getCurrentWalletState();
      if (state.network && !state.isSupportedNetwork) {
        console.log("Network changed to unsupported");
        that.toast.error("Switched network not supported");
      }
    });
  }

  /* ---------- connect ---------- */
  async connectWallet(type?: "metaMask" | "walletConnect") {
    return await this.appKitService.connectWallet(type);
  }

  /* ---------- disconnect ---------- */
  async disconnect(): Promise<void> {
    return await this.appKitService.disconnect();
  }

  getCurrentAccount() {
    return this.getAccount();
  }

  /* ---------- getAccount ---------- */
  getAccount() {
    try {
      const config = this.appKitService.getWagmiConfig();
      return getAccount(config);
    } catch (e) {
      return { isConnected: false, address: undefined, chainId: undefined } as any;
    }
  }

  /* ---------- getAddress ---------- */
  getAddress(): string | undefined {
    return this.getAccount().address
  }

  /* ---------- ensureConnected ---------- */
  /**
   * Restores the wallet session if it was dropped (e.g. after a hard refresh).
   * Throws when the wallet is still not connected, so the caller surfaces a clear
   * message rather than failing later inside the wallet SDK.
   */
  async ensureConnected(): Promise<any> {
    return await this.appKitService.ensureConnection();
  }

  /** Same, but resolves to null instead of throwing when nothing is connected */
  async tryEnsureConnected(): Promise<any | null> {
    return await this.appKitService.tryEnsureConnection();
  }

  /* ---------- getSigner (WalletClient) ---------- */
  async getSigner(): Promise<WalletClient | null> {
    const account = await this.tryEnsureConnected();
    if (!account || !account.connector)
      return null

    const config = this.appKitService.getWagmiConfig();
    return await getWalletClient(config)
  }

  /* ---------- signedText ---------- */
  async signText(message: string): Promise<Hex> {
    await this.ensureConnected();
    const state = this.appKitService.getCurrentWalletState();

    if (state.isConnected && !state.isSupportedNetwork) {
      this.toast.error('Please switch to Polygon Amoy');
      throw new Error('Wrong network');
    }

    const config = this.appKitService.getWagmiConfig();
    return signMessage(config, { message })
  }

  /* ---------- createSignature ---------- */
  async createSignature(message: string): Promise<Hex> {
    return this.signText(message)
  }

  async generateSignature(): Promise<any> {
    return this.createTypedSignature()
  }

  async createTypedSignature(payload?: any): Promise<any> {
    await this.ensureConnected();
    const state = this.appKitService.getCurrentWalletState();

    if (state.isConnected && !state.isSupportedNetwork) {
      this.toast.error('Please switch to Polygon Amoy');
      throw new Error('Wrong network');
    }

    // FIX: Use state.chainId instead of state.network (which is a string)
    const chainId = Number(state.chainId);

    if (!payload) {
      payload = {
        domain: {
          ...ENABLE_TRADING_SIGNATURE.domain,
          chainId: chainId
        },
        types: ENABLE_TRADING_SIGNATURE.types,
        primaryType: ENABLE_TRADING_SIGNATURE.primaryType,
        message: ENABLE_TRADING_SIGNATURE.message
      };
    }

    const config = this.appKitService.getWagmiConfig();
    return await signTypedData(config, payload);
  }

  getSignatureParams(signature: Hex): any {
    const sig = signature.startsWith('0x') ? signature.slice(2) : signature;

    const r = '0x' + sig.slice(0, 64);
    const s = '0x' + sig.slice(64, 128);
    const v = parseInt(sig.slice(128, 130), 16);

    return { v, r, s }
  }

  public async waitForWalletInitialization(): Promise<void> {
    await this.appKitService.waitForInitialization();
  }

  public async signTypedData(payload: any) {
    return this.createTypedSignature(payload);
  }

  async signGnosisSafeHash(txHash: string): Promise<string> {
    await this.ensureConnected();
    const config = this.appKitService.getWagmiConfig();

    if (!txHash.startsWith('0x')) {
      throw new Error('txHash must be 0x prefixed');
    }

    const signature = await signMessage(config, {
      message: { raw: txHash as `0x${string}` }
    });

    return this.formatGnosisSafeSignature(signature);
  }

  public formatGnosisSafeSignature(signature: string): string {
    const sig = signature.startsWith('0x') ? signature.slice(2) : signature;

    if (sig.length !== 130) {
      throw new Error('Invalid signature length');
    }

    const r = sig.slice(0, 64);
    const s = sig.slice(64, 128);
    let v = parseInt(sig.slice(128, 130), 16);

    if (v < 27) v += 27;
    if (v === 27 || v === 28) v += 4;

    return '0x' + r + s + v.toString(16).padStart(2, '0');
  }

  isValidAddress(address: string): boolean {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  }
}
